import { inject, Injectable, OnDestroy } from '@angular/core';
import { LenderService } from '@shared/service/lender/lender.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { OaoPageHelper } from '../../helper/oao-page.helper';
import { TokenService } from '@cudirect/shared-ui-library';
import { Subscription } from 'rxjs';
import { StatefulService } from '@app/service/stateful.service';
import { AlloyService } from '@workflow/ecp-workflow/dynamic-app/service/alloy/alloy.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OaoVisibleFunctionsService implements OnDestroy {
  private readonly statefulService = inject(StatefulService);

  private readonly subscriptions = new Subscription();
  private token = null;

  constructor(
    private readonly lenderService: LenderService,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly tokenService: TokenService,
    private readonly alloyService: AlloyService,
    private readonly workflowService: WorkflowService
  ) {
    const tokenSub = this.tokenService.getToken().subscribe((token) => {
      this.token = token;
    });
    this.subscriptions.add(tokenSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Unified prefill visibility logic (Instatouch or Alloy) for OAO.
   * Rules:
   * 1. lender.enablePrefill must be true.
   * 2. Determine if autofill journey is enabled for membership:
   *    - If lender.enableAlloyPrefill === true and alloyEnabled for membership return true
   *    - Else check if instatouch enabled for membership
   */

  public isOAOPrefillEnabled = (context: LogicFnContext): boolean => {
    const lender = this.lenderService?.lenderData;
    const adminConfig = this.adminPortalFeaturesService?.config;
    const oaoFeatures = adminConfig?.oaoFeatures;

    if (lender?.enableAlloyPrefill) {
      const enabled = this.alloyService.isAlloyEnabled()?.enabled ?? false;
      if (!enabled) return false;

      // Suppress if Alloy journey already completed
      const alloyCompleted = this.statefulService.wrap('api:journeyInfo/isAlloyCompleted').value === true;
      if (alloyCompleted) return false;

      const membershipId = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value;
      const membership = adminConfig?.oaoConfiguration?.memberships?.find((m) => m.membershipID === membershipId);
      const journeyId = membership?.alloyJourneyId;
      const journey = journeyId ? lender?.alloySettings?.journeys?.find((j) => j.id === journeyId) : undefined;
      if (journey && journey.enableAlloyPrefill === false) {
        return false;
      }
      const currentPage = this.workflowService.currentPage;
      const pageIdStr = (currentPage?.config?.id || currentPage?.id || '').toLowerCase();
      const isCoApplicant = pageIdStr.includes('co-applicant') && !pageIdStr.includes('additional-co-app');
      const isAdditionalCoApplicant = pageIdStr.includes('additional-co-app');
      const isPrimary = !(isCoApplicant || isAdditionalCoApplicant);
      if (!isPrimary && journey && journey.allowCoApplicants === false) {
        return false;
      }
      return enabled;
    }

    return oaoFeatures?.enableInstatouch ?? false;
  };

  public showOAOCard = (context: LogicFnContext): boolean => {
    const cardId = context.card.id;
    const lstExcludedCards =
      this.lenderService && this.lenderService.lenderDataInfo && (this.lenderService.lenderDataInfo.excludedCards as any[]);

    if (lstExcludedCards && lstExcludedCards.indexOf(cardId) !== -1) {
      return false;
    }

    return true;
  };

  public showBeneficiarySameAsPrimary = (context: LogicFnContext, primaryAddressPath: string, usResidentPath: string): boolean => {
    const addressLength = this.statefulService.arraySize(primaryAddressPath);
    if (addressLength > 0) {
      const isUsResident = this.statefulService.wrap(usResidentPath).value;
      return !isUsResident;
    }
    return true;
  };

  /** Method to check any of bsa (Products & Services Offerings) checkbox checked */
  public showBSAProductserviceHasValue = (context: LogicFnContext): boolean => {
    const storagePathPrefix = 'api:oaoMemberInformation/businessInformation/bankSecrecyAct/productsAndServicesOfferingsList';
    const checkcashing = this.statefulService.wrap(`${storagePathPrefix}/checkcashing`).value as boolean;
    const moneytransmission = this.statefulService.wrap(`${storagePathPrefix}/moneytransmission`).value as boolean;
    const currencyorVirtualCurrency = this.statefulService.wrap(`${storagePathPrefix}/currencyorVirtualCurrency`).value as boolean;
    const saleorStoredCards = this.statefulService.wrap(`${storagePathPrefix}/saleorStoredCards`).value as boolean;
    const issuerorSeller = this.statefulService.wrap(`${storagePathPrefix}/issuerorSeller`).value as boolean;
    if (checkcashing || moneytransmission || currencyorVirtualCurrency || saleorStoredCards || issuerorSeller) {
      return true;
    }
    return false;
  };

  public showOaoClarification = (context: LogicFnContext, phoneType: string): boolean => {
    const contactInfo = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/phoneType').value;
    if (contactInfo !== phoneType) {
      return false;
    }

    return !this.token;

    // const isOidTokenExist = (): boolean => {
    //   return this.tokenService.getCachedToken() !== null;
    // };
    // return !isOidTokenExist();
  };
}
